import React, { useCallback, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthContext, AuthState } from './AuthContext';
import { configureTokenProvider } from '../api/client';
import { oktaAuth } from './oktaConfig';
import { getIdentityUser, IdentityUser } from './identityService';
import { apiScopes, loginRequest } from './msalConfig';
import { ApiError } from '../types';

// ─── Shared QueryClient ───────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      // 401/403 are already retried once by the API client with a fresh token — don't repeat.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && (error.status === 401 || error.status === 403))
        && failureCount < 1,
      refetchOnWindowFocus: true,
    },
  },
});

interface AuthProviderProps {
  children: React.ReactNode;
}

// ─── Dev bypass: local sign-in with app roles ────────────────────────────────
const DEV_USER = {
  name: 'Bhawna Hooda',
  email: 'Bhawna.Hooda@freeman.com',
  roles: ['BRG-Lead'],
};

function DevAuthProvider({ children }: AuthProviderProps) {
  const [loggedIn, setLoggedIn] = useState(false); // Require manual login

  const getAccessToken = async () => 'dev-bypass-token';

  const devAuth: AuthState = {
    isAuthenticated: loggedIn,
    loading: false,
    name: DEV_USER.name,
    email: DEV_USER.email,
    roles: DEV_USER.roles,
    login: async () => {
      setLoggedIn(true);
    },
    logout: () => {
      queryClient.clear();
      setLoggedIn(false);
    },
    getAccessToken,
    refreshAuth: async () => {},
  };

  useLayoutEffect(() => {
    // Configure token provider BEFORE children mount (runs in layout phase)
    configureTokenProvider(getAccessToken, devAuth);
  }, []);

  return (
    <AuthContext.Provider value={devAuth}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </AuthContext.Provider>
  );
}

// ─── MSAL bridge: reads real MSAL state and exposes it via AuthContext ─────────
export function MsalAuthBridge({ children }: AuthProviderProps) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] ?? null;

  const roles: string[] =
    (account?.idTokenClaims?.roles as string[] | undefined) ?? [];

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error('No account available');
    try {
      const result = await instance.acquireTokenSilent({ scopes: apiScopes, account });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup({ scopes: apiScopes, account });
        return result.accessToken;
      }
      throw error;
    }
  }, [account, instance]);

  const auth: AuthState = {
    isAuthenticated,
    loading: false, // MSAL manages auth state synchronously
    name:  account?.name ?? null,
    email: account?.username ?? null,
    roles,
    login,
    logout,
    getAccessToken,
    refreshAuth: async () => {},
  };

  // Wire the token provider so apiClient uses real MSAL tokens
  useEffect(() => {
    configureTokenProvider(getAccessToken, auth);
  }, [getAccessToken, auth]);

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Okta Auth Provider ─────────────────────────────────────────────────────
function OktaAuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<{ name: string; email: string } | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const getStoredEnterpriseToken = useCallback((): string | null => {
    try {
      const token = sessionStorage.getItem('tfc_access_token');
      if (!token) return null;

      const issuedAtRaw = sessionStorage.getItem('tfc_token_issued_at');
      const expiresInRaw = sessionStorage.getItem('tfc_token_expires_in');

      // If expiry metadata is absent, use the token as-is.
      if (!issuedAtRaw || !expiresInRaw) {
        return token;
      }

      const issuedAt = Number(issuedAtRaw);
      const expiresIn = Number(expiresInRaw);
      if (!Number.isFinite(issuedAt) || !Number.isFinite(expiresIn)) {
        return token;
      }

      // Refresh 30 seconds early.
      const expiresAt = issuedAt + (expiresIn * 1000);
      const isExpired = Date.now() >= (expiresAt - 30000);
      return isExpired ? null : token;
    } catch {
      return null;
    }
  }, []);

  // Resolve user details + roles from the Freeman Identity Service
  const resolveIdentity = useCallback(async (): Promise<IdentityUser | null> => {
    try {
      const tokenManager = oktaAuth.tokenManager;
      const accessToken = await tokenManager.get('accessToken');
      if (!accessToken || !('accessToken' in accessToken)) {
        console.warn('[Okta] No access token available');
        return null;
      }

      const identityUser = await getIdentityUser(accessToken.accessToken);
      // console.log('[Okta] Identity service returned:', identityUser);
      return identityUser;
    } catch (error: any) {
      // 401 means token is invalid/expired - treat as unauthenticated
      if (error?.message?.includes('401')) {
        console.warn('[Okta] Access token expired or invalid (401) - clearing auth');
        return null;
      }
      console.error('[Okta] Identity service call failed:', error);
      return null;
    }
  }, []);

  // Fallback: extract user info from Okta token claims when identity service is unavailable
  const resolveFromTokenClaims = useCallback(async (): Promise<IdentityUser | null> => {
    try {
      // Prefer access token claims because API authorization depends on access-token groups.
      const accessToken = await oktaAuth.tokenManager.get('accessToken');
      const idToken = await oktaAuth.tokenManager.get('idToken');

      const accessClaims = accessToken && 'claims' in accessToken
        ? (accessToken.claims as Record<string, any>)
        : null;
      const idClaims = idToken && 'claims' in idToken
        ? (idToken.claims as Record<string, any>)
        : null;

      const claims = accessClaims ?? idClaims;
      if (!claims) return null;

      const name = claims.name || claims.preferred_username || claims.email || '';
      const email = claims.email || claims.sub || '';
      const userId = claims.sub || '';

      // Extract roles from groups claim (Okta Org AS includes groups with 'groups' scope)
      const groups: string[] = claims.groups || [];
      const roles = groups
        .filter((g: string) => /^(BRG[-_.]|Okta-App-TFC-BRG)/i.test(g))
        .map((g: string) => {
          // "Okta-App-TFC-BRG-Admin-Dev" → "BRG.Admin"
          const match = g.match(/BRG[-_.]?(Admin|Leader|Member)/i);
          if (match) return `BRG.${match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase()}`;
          return g.replace(/^BRG[-_]/, 'BRG.');
        });

      // console.log('[Okta] Token claims fallback - groups:', groups, '→ roles:', roles);
      if (roles.length === 0) return null;

      return { userId, name, email, roles };
    } catch (error) {
      console.error('[Okta] Token claims fallback failed:', error);
      return null;
    }
  }, []);

  // Core auth verification: check Okta tokens + resolve identity
  const verifyAuth = useCallback(async (): Promise<boolean> => {
    try {
      const authenticated = await oktaAuth.isAuthenticated();
      // console.log('[OktaAuth] isAuthenticated:', authenticated);

      if (!authenticated) {
        // Check if tokens exist in storage but SDK says not authenticated
        // const storedAccess = await oktaAuth.tokenManager.get('accessToken');
        // const storedId = await oktaAuth.tokenManager.get('idToken');
        // console.log('[OktaAuth] Not authenticated. Stored tokens:', {
        //   hasAccessToken: !!(storedAccess && 'accessToken' in storedAccess),
        //   hasIdToken: !!(storedId && 'claims' in storedId),
        //   localStorage: typeof localStorage !== 'undefined' ? 'available' : 'unavailable',
        //   lsKeys: typeof localStorage !== 'undefined' ? Object.keys(localStorage).filter(k => k.includes('okta')).join(', ') : 'N/A',
        // });
        setIsAuthenticated(false);
        setUser(null);
        setRoles([]);
        return false;
      }

      // Try identity service first, fall back to token claims
      let identityUser = await resolveIdentity();
      if (!identityUser || identityUser.roles.length === 0) {
        // console.log('[OktaAuth] Identity service unavailable or returned no roles, trying token claims...');
        identityUser = await resolveFromTokenClaims();
      }

      if (identityUser && identityUser.roles.length > 0) {
        setIsAuthenticated(true);
        setUser({ name: identityUser.name, email: identityUser.email });
        setRoles(identityUser.roles);
        // console.log('[OktaAuth] ✓ User authenticated with roles:', identityUser.roles);
        return true;
      }

      // Authenticated but not authorized for BRG if no roles/groups are present.
      console.warn('[Okta] Authenticated user has no BRG roles/groups in identity response or token claims.');
      setIsAuthenticated(false);
      setUser(null);
      setRoles([]);
      return false;
    } catch (error) {
      console.error('[Okta] Auth check failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      setRoles([]);
      return false;
    }
  }, [resolveIdentity, resolveFromTokenClaims]);

  // refreshAuth: callable from callback page to update auth state without page reload
  // IMPORTANT: Do NOT set loading=true here - that unmounts children (including the router)
  const refreshAuth = useCallback(async (): Promise<void> => {
    await verifyAuth();
  }, [verifyAuth]);

  // Check authentication state on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Skip auth check on callback and logout pages (they handle auth themselves)
        if (window.location.pathname === '/login/callback' || window.location.pathname === '/logout') {
          setLoading(false);
          return;
        }

        // Check if URL has OAuth parameters (code, state, error) but we're NOT on callback page
        const urlParams = new URLSearchParams(window.location.search);
        const hasOAuthParams = urlParams.has('code') || urlParams.has('state') || urlParams.has('error');
        
        if (hasOAuthParams && window.location.pathname !== '/login/callback') {
          // console.warn('[Okta] OAuth parameters detected on non-callback page. Cleaning URL.');
          window.history.replaceState({}, document.title, window.location.pathname);
          setLoading(false);
          return;
        }

        await verifyAuth();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [verifyAuth]);

  const login = useCallback(async (loginHint?: string) => {
    try {
      // console.log('[Okta] Starting login redirect with loginHint:', loginHint);
      
      // Clear token manager to force fresh authentication
      await oktaAuth.tokenManager.clear();
      // console.log('[Okta] Token manager cleared');
      
      // Redirect to Okta
      await oktaAuth.signInWithRedirect({
        ...(loginHint ? { loginHint } : {}),
      });
      
    } catch (error: any) {
      console.error('[Okta] Login failed:', error?.message || error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // console.log('[Okta] Starting logout...');
      
      // Clear local auth state immediately
      setIsAuthenticated(false);
      setUser(null);
      setRoles([]);
      
      // Clear React Query cache
      queryClient.clear();
      
      // Clear token manager to prevent stale token calls
      await oktaAuth.tokenManager.clear();
      
      // Sign out from Okta
      await oktaAuth.signOut();
      
      // console.log('[Okta] Logout completed');
    } catch (error) {
      console.error('[Okta] Logout error:', error);
      // Clear local state anyway
      setIsAuthenticated(false);
      setUser(null);
      setRoles([]);
      queryClient.clear();
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string> => {
    try {
      // Preferred: enterprise token from identity service (contains app groups/roles).
      const enterpriseToken = getStoredEnterpriseToken();
      if (enterpriseToken) {
        return enterpriseToken;
      }

      // If enterprise token is missing/expired, re-exchange using current Okta token.
      const refreshedIdentity = await resolveIdentity();
      if (refreshedIdentity?.enterpriseAccessToken) {
        return refreshedIdentity.enterpriseAccessToken;
      }

      const tokenManager = oktaAuth.tokenManager;
      
      // Fallback only: raw Okta access token.
      const accessToken = await tokenManager.get('accessToken');
      if (accessToken && 'accessToken' in accessToken) {
        console.warn('[Okta] Falling back to raw Okta access token for API call. Enterprise token unavailable.');
        return accessToken.accessToken;
      }
      
      // If no token, try to renew
      await oktaAuth.tokenManager.renew('accessToken');
      const renewedToken = await tokenManager.get('accessToken');
      if (renewedToken && 'accessToken' in renewedToken) {
        console.warn('[Okta] Falling back to renewed raw Okta access token for API call. Enterprise token unavailable.');
        return renewedToken.accessToken;
      }
      
      throw new Error('No token available');
    } catch (error) {
      console.error('[Okta] Failed to get token:', error);
      throw error;
    }
  }, [getStoredEnterpriseToken, resolveIdentity]);

  // Fresh Enterprise token for the API client's one-time retry on 401 / role-check 403.
  // A new okta-login exchange writes BRG's roles back to the Identity Service, which
  // another Freeman app's sign-in may have replaced.
  const refreshEnterpriseToken = useCallback(async (): Promise<string> => {
    const identity = await resolveIdentity();
    if (!identity?.enterpriseAccessToken) {
      throw new Error('Could not get a fresh Enterprise token');
    }
    return identity.enterpriseAccessToken;
  }, [resolveIdentity]);

  const auth: AuthState = useMemo(() => ({
    isAuthenticated,
    loading,
    name: user?.name ?? null,
    email: user?.email ?? null,
    roles,
    login,
    logout,
    getAccessToken,
    refreshAuth,
  }), [isAuthenticated, loading, user, roles, login, logout, getAccessToken, refreshAuth]);

  // Configure token provider synchronously during render (not in useEffect).
  // React fires child effects before parent effects, so a useEffect here would
  // run AFTER CalendarPage's useEffect, causing "Token provider not configured"
  // errors on page refresh. This is safe because configureTokenProvider is
  // idempotent and doesn't trigger state updates.
  if (isAuthenticated) {
    configureTokenProvider(getAccessToken, auth, refreshEnterpriseToken);
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading authentication...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </AuthContext.Provider>
  );
}

// ─── Public AuthProvider — uses Okta mode ────────────────────────────────────
export function AuthProvider({ children }: AuthProviderProps) {
  // Check if dev auth bypass is enabled
  const devBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  
  // Debug log
  if (typeof window !== 'undefined') {
    //console.log('Auth Mode:', {
      //VITE_DEV_AUTH_BYPASS: import.meta.env.VITE_DEV_AUTH_BYPASS,
      //devBypass,
      //MODE: import.meta.env.MODE,
      //provider: devBypass ? 'DevAuthProvider' : 'OktaAuthProvider'
    //});
  }

  if (devBypass) {
    return (
      <DevAuthProvider>
        {children}
      </DevAuthProvider>
    );
  }

  return (
    <OktaAuthProvider>
      {children}
    </OktaAuthProvider>
  );
}
